/* eslint-disable max-len */
import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, Calendar, Mail } from 'lucide-react';
import { Navbar, Sidebar } from '../../components';
import './teamEngagement.css';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import DatePicker from '../../components/datePicker';
import TeamMemberCard from './TeamMemberCard';
import TeamEngagementScoreCard from './TeamEngagementScoreCard';
import TeamActivityCard from './TeamActivityCard';
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';
import TimezoneDropdown from '../../components/selectors/TimezoneDropdown';
import ScheduleEmailModal from './ScheduleEmailModal';

function CalendarCaret(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Calendar {...props} size={14} color="#53A551" />;
}

// Future dates have no activity data to show
const disableFutureDates = (d) => moment(d).isAfter(moment(), 'day');

function TeamView() {
  const { teamId } = useParams();
  const { search } = useLocation();
  const {
    activeWorkspace, redirect, member, user,
  } = useContext(UserContext);
  const [team, setTeam] = useState({});
  const today = useMemo(() => new Date(), []);
  // Single-day view (per the redesign prototype): the range is always
  // [selected day, selected day]
  const [date, setDate] = useState([today, today]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(
    activeWorkspace?.timezone ?? 'Members timezone',
  );

  useEffect(() => {
    // The list page links here with its ?start/&end range; this view is
    // single-day, so use the most recent day of that range (capped at today).
    const params = new URLSearchParams(search);
    const end = params.get('end');
    if (end) {
      const endMoment = moment(end, 'YYYY-MM-DD HH:mm:ss');
      const day = (endMoment.isAfter(moment(), 'day') ? moment() : endMoment).toDate();
      setDate([day, day]);
    }
  }, [search]);

  useEffect(() => {
    if (activeWorkspace?.timezone) {
      setSelectedTimezone(activeWorkspace.timezone);
    }
  }, [activeWorkspace?.timezone]);

  const fetchTeam = async () => {
    setIsLoading(true);
    const [startDate, endDate] = date;
    const startOfDay = moment(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getTeam(activeWorkspace.id, teamId),
      timeout: 3000000,
      params: {
        selectedTimezone,
        start: startOfDay,
        end: endOfDay,
      },
    });
    if (success) {
      setTeam(data.team);
    }
    setIsLoading(false);
  };

  const handleSelectDate = (_dateRange) => {
    setDate(_dateRange);
  };

  const handleSelectTimezone = async (timezone) => {
    setSelectedTimezone(timezone);
  };

  useEffect(() => {
    const isWorkspaceAdmin = getMemberRoleLevel(member) >= accountTypeLevel['Workspace admin'];
    if (isWorkspaceAdmin) return;
    const isTeamManager = member.teamMates.some((mate) => mate.teamId === parseInt(teamId, 10));
    if (!isTeamManager) redirect('/workspaces');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedTimezone]);

  const timezoneOptions = [
    {
      heading: 'Members timezone',
      description: 'Members timezone',
      value: 'Members timezone',
    },
    {
      heading: 'Your timezone',
      description: user.timezone,
      value: user.timezone,
    },
    {
      heading: 'Workspace timezone',
      description: activeWorkspace.timezone ?? 'Etc/UTC',
      value: activeWorkspace.timezone ?? 'Etc/UTC',
    },
    {
      heading: 'Global timezone',
      description: 'Etc/UTC',
      value: 'Etc/UTC',
    },
  ];

  return (
    <div>
      {showScheduleModal && (
        <ScheduleEmailModal
          closeModal={() => setShowScheduleModal(false)}
          teamId={teamId}
          teamName={team.name}
          timezoneOptions={timezoneOptions}
        />
      )}
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex items-center gap-2 flex-1">
              <button
                type="button"
                onClick={() => redirect('/members/team-engagement')}
                aria-label="Back"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-brand-500 hover:bg-surface-1 transition flex-shrink-0"
              >
                <ArrowLeft size={20} />
              </button>
              <h3
                className="text-ink-900 font-bold"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '22px',
                  lineHeight: '28px',
                  margin: 0,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {team.name}
                {' '}
                Engagement
              </h3>
            </div>
            <div className="team-view-header-actions flex flex-nowrap items-center gap-2 sm:gap-3 sm:justify-end w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowScheduleModal(true)}
                className="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:bg-brand-700 transition shadow-[0_1px_2px_rgba(14,30,64,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 whitespace-nowrap flex-shrink-0"
              >
                <Mail size={16} />
                Schedule Email
              </button>
              <DatePicker
                format="EEE, MMM d, yyyy"
                value={date[0]}
                onChange={(d) => handleSelectDate([d, d])}
                oneTap
                editable={false}
                placement="bottomEnd"
                caretAs={CalendarCaret}
                shouldDisableDate={disableFutureDates}
                className="w-auto"
              />
              <TimezoneDropdown
                value={selectedTimezone}
                onChange={handleSelectTimezone}
                options={timezoneOptions}
              />
            </div>
          </div>
        </div>

        <div className="screen-body">
          {isLoading && (
            <div className="loader-container">
              <CircularProgress sx={{ color: 'inherit' }} />
            </div>
          )}
          {!isLoading && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <TeamMemberCard team={team} />
                <TeamEngagementScoreCard team={team} />
              </div>
              <TeamActivityCard
                team={team}
                timezone={selectedTimezone}
                curDate={moment(date?.[0] || new Date()).startOf('day').format('YYYY-MM-DD HH:mm:ss')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamView;
